/**
 * WaveTag guides page.
 *
 * Whether a guide is open is decided by CSS alone (the URL hash matches the id of a `.wtg-guide`, see wavetag-guides.css).
 * This script only adds what CSS cannot do: search across guides and FAQ, previous/next links, scroll handling,
 * external links in a new tab and theme editor support. Listeners sit on `document`, so sections re-rendered by the
 * theme editor keep working.
 */

/** @param {string} value */
const normalize = (value) => value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * @param {string} selector
 * @param {ParentNode} [root]
 * @returns {HTMLElement[]}
 */
const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));

/** @type {WeakMap<Element, string>} */
const haystacks = new WeakMap();

/** @param {Element} element */
function haystack(element) {
  let text = haystacks.get(element);
  if (text === undefined) {
    text = normalize(element.textContent ?? '');
    haystacks.set(element, text);
  }
  return text;
}

/** @returns {HTMLElement | null} */
function openGuide() {
  const id = decodeURIComponent(location.hash.slice(1));
  const target = id ? document.getElementById(id) : null;
  return target?.classList.contains('wtg-guide') ? target : null;
}

/** Fills the previous/next links of every guide with the neighbouring guides in page order. */
function updatePagers() {
  const guides = all('.wtg-guide');

  guides.forEach((guide, index) => {
    for (const [selector, neighbour] of [
      ['[data-wtg-prev]', guides[index - 1]],
      ['[data-wtg-next]', guides[index + 1]],
    ]) {
      const link = /** @type {HTMLAnchorElement | null} */ (guide.querySelector(selector));
      if (!link) continue;

      link.hidden = !neighbour;
      if (!neighbour) continue;

      link.href = `#${neighbour.id}`;
      const title = link.querySelector('[data-wtg-pager-title]');
      if (title) title.textContent = neighbour.dataset.title ?? '';
    }
  });
}

/** Links that leave the site open in a new tab so the reader does not lose the guide. */
function markExternalLinks() {
  all('.wtg-rte a[href]').forEach((link) => {
    const anchor = /** @type {HTMLAnchorElement} */ (link);
    if (anchor.origin === location.origin || !/^https?:$/.test(anchor.protocol)) return;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
  });
}

/** @param {string} query */
function search(query) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const active = words.length > 0;
  let found = 0;

  all('.wtg-guide, .wtg-faq').forEach((item) => {
    const match = !active || words.every((word) => haystack(item).includes(word));
    item.toggleAttribute('data-filtered', !match);
    if (match) found++;
  });

  all('.wtg-list').forEach((list) => {
    const empty = active && !list.querySelector('.wtg-guide:not([data-filtered]), .wtg-faq:not([data-filtered])');
    list.toggleAttribute('data-empty', empty);
  });

  const status = document.querySelector('[data-wtg-status]');
  if (!(status instanceof HTMLElement)) return;

  if (!active) {
    status.textContent = '';
  } else if (found === 0) {
    status.innerHTML = '';
    const strong = document.createElement('strong');
    strong.textContent = status.dataset.none ?? '';
    status.append(strong, ' ', status.dataset.hint ?? '');
  } else {
    status.textContent = (status.dataset.found ?? '').replace('__NUMBER__', String(found));
  }
}

function init() {
  updatePagers();
  markExternalLinks();

  const searchBox = document.querySelector('[data-wtg-search]');
  if (searchBox instanceof HTMLElement) {
    searchBox.hidden = false;
    const input = searchBox.querySelector('input');
    if (input && input.value) search(input.value);
  }
}

/** @type {string | null} */
let lastGuideId = null;

function handleHash() {
  const guide = openGuide();

  if (guide) {
    lastGuideId = guide.id;
    window.scrollTo({ top: 0, behavior: 'instant' });
    guide.querySelector('.wtg-article__title')?.focus({ preventScroll: true });
  } else if (lastGuideId) {
    // Back to the overview: bring the card of the guide that was just closed into view
    const card = document.getElementById(lastGuideId)?.querySelector('.wtg-card');
    lastGuideId = null;
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ block: 'center', behavior: 'instant' });
      card.focus({ preventScroll: true });
    }
  }
}

document.addEventListener('input', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.closest('[data-wtg-search]')) {
    search(event.target.value);
  }
});

document.addEventListener('submit', (event) => {
  if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-wtg-search]')) return;
  event.preventDefault();
  // Enter opens the first guide that matches
  const first = document.querySelector('.wtg-guide:not([data-filtered])');
  if (first?.id) location.hash = first.id;
});

window.addEventListener('hashchange', handleHash);

// Theme editor: selecting a guide (or one of its steps) opens it, selecting anything else shows the overview again
document.addEventListener('shopify:block:select', (event) => {
  const guide = event.target instanceof Element ? event.target.closest('.wtg-guide') : null;
  if (guide?.id) location.hash = guide.id;
});

document.addEventListener('shopify:section:select', (event) => {
  const open = openGuide();
  if (open && event.target instanceof Element && !event.target.contains(open)) location.hash = '';
});

document.addEventListener('shopify:section:load', init);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init();
    if (openGuide()) requestAnimationFrame(handleHash);
  });
} else {
  init();
  if (openGuide()) requestAnimationFrame(handleHash);
}
